"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { STEPS, Platform, Method, Section, Step } from "@/lib/esim/guide-steps";

const HERO_COPY: Record<Section | "full", string> = {
  full: "Install and activate your Vell eSIM in minutes.",
  install: "Follow these steps to install your Vell eSIM profile.",
  activate: "Turn on your Vell eSIM line and get connected.",
};

const HERO_TITLE: Record<Section | "full", { main: string; accent: string }> = {
  full:     { main: "Get connected",   accent: "anywhere" },
  install:  { main: "Install your",    accent: "Vell eSIM" },
  activate: { main: "Activate your",   accent: "Vell eSIM" },
};

const BASE = "/esim/";

const YOUTUBE: Record<Platform, string> = {
  ios:     "jHJz9hS3mcU",
  android: "rP_eu7jP2lo",
};

function getSteps(plat: Platform, sec: Section, meth: Method): Step[] {
  if (sec === "activate") return STEPS[plat].activate as Step[];
  return (STEPS[plat].install as Record<Method, Step[]>)[meth];
}


function VideoSection({ platform }: { platform: Platform }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-[12px] border border-[#E2DED6] overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full lg:w-full flex items-center justify-between px-4 sm:px-5 py-4 bg-brand-text/5
          hover:bg-brand-text/10 transition-colors duration-150 text-left cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <span
            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: "#0F0F0F" }}
          >
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
              <path d="M2.5 1.5l7 4-7 4V1.5z" fill="#F5F3EE" />
            </svg>
          </span>
          <div>
            <p className="text-sm 3xl:text-2xl font-bold text-brand-card leading-tight m-0">
              {platform === "ios" ? "iPhone" : "Android"} video walkthrough
            </p>
            <p className="text-xs 3xl:text-xl text-[#5c5c5c] mt-0.5 m-0">
              Watch the full installation in under 3 minutes
            </p>
          </div>
        </div>
        <svg
          width="16" height="16" viewBox="0 0 16 16" fill="none"
          className={`flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        >
          <path d="M3 5.5l5 5 5-5" stroke="#999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${
          open ? "opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-4 sm:px-5 pt-4 pb-5 bg-brand-text/5 border-t border-[#E2DED6]">
          <div
            className="relative w-full rounded-[10px] overflow-hidden"
            style={{ aspectRatio: "16/9" }}
          >
            {open && (
              <iframe
                src={`https://www.youtube.com/embed/${YOUTUBE[platform]}?rel=0&modestbranding=1`}
                title={`${platform === "ios" ? "iPhone" : "Android"} eSIM installation walkthrough`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              />
            )}
          </div>
          <p className="text-sm 3xl:text-xl text-[#999] mt-3 leading-relaxed m-0">
            This video demonstrates the complete {platform === "ios" ? "iPhone" : "Android"} installation
            and activation flow.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function EsimGuideContent({
  showHero = true,
  lockedSection,
}: {
  showHero?: boolean;
  /** When set, the page only shows this section and hides the Install/Activate switcher. */
  lockedSection?: Section;
}) {
  const [platform, setPlatform] = useState<Platform>("ios");
  const [section,  setSection]  = useState<Section>(lockedSection ?? "install");
  const method: Method = "qr";
  const [step,     setStep]     = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    if (!lightboxOpen) return;
    document.body.style.overflow = "hidden";
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") setLightboxOpen(false); }
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [lightboxOpen]);

  const steps   = getSteps(platform, section, method);
  const current = steps[step];
  const isFirst = step === 0;
  const isLast  = step === steps.length - 1;

  function advance()                { if (!isLast)       setStep((s) => s + 1); }
  function back()                   { if (!isFirst)      setStep((s) => s - 1); }
  function jump(i: number)          { setStep(i); }
  function switchPlatform(p: Platform) { setPlatform(p); setStep(0); }
  function switchSection(s: Section)   { setSection(s);  setStep(0); }

  const progress = ((step + 1) / steps.length) * 100;

  return (
    <div className="min-h-screen bg-brand-text ">
      {showHero && <header className="relative bg-brand-bg px-6 py-5 sm:py-20 xl:py-24 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,1) 1px,transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="relative max-w-2xl mx-auto text-center">
          <p
            className="text-xs 3xl:text-base tracking-[0.22em] text-brand-text/80 uppercase mb-5"
          >
            eSIM Setup Guide
          </p>
          <h1
            className="text-5xl lg:text-6xl xl:text-7xl 3xl:text-8xl font-bold text-brand-text leading-[1.06] tracking-[-0.03em] mb-4"
          >
            {HERO_TITLE[lockedSection ?? "full"].main}{" "}
            <span className="text-brand-text/70">{HERO_TITLE[lockedSection ?? "full"].accent}</span>
          </h1>
          <p className="text-sm 3xl:text-xl text-brand-text/80 leading-relaxed max-w-md mx-auto m-0">
            {HERO_COPY[lockedSection ?? "full"]}
          </p>
        </div>
      </header>}
      <div className="sticky top-0 z-30 bg-brand-text/30 border-b border-[#D8D4C8]">
        <div className="max-w-[960px] mx-auto px-4 sm:px-6 flex justify-center gap-6 lg:gap-14">
          {(["ios","android"] as Platform[]).map((p) => (
            <button
              key={p}
              onClick={() => switchPlatform(p)}
              className={`flex items-center gap-2 px-5 sm:px-7 py-[17px] border-b-2 text-sm 3xl:text-2xl
                tracking-[0.02em] cursor-pointer transition-all duration-150 bg-transparent
                ${platform === p
                  ? "border-[#0F0F0F] font-bold text-[#0F0F0F]"
                  : "border-transparent font-medium text-[#595959] hover:text-[#555]"
                }`}
            >
              {p === "ios" ? (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11" />
                </svg>
              ) : (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zm11.5-10C18.33 8 19 8.67 19 9.5v7c0 .83-.67 1.5-1.5 1.5S16 17.33 16 16.5v-7c0-.83.67-1.5 1.5-1.5zm-13 0C3.67 8 3 8.67 3 9.5v7c0 .83.67 1.5 1.5 1.5S6 17.33 6 16.5v-7C6 8.67 5.33 8 4.5 8zm11.03-5.84 1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48A6.93 6.93 0 0 0 12 1c-.96 0-1.86.23-2.66.63L7.85.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31A6.92 6.92 0 0 0 6 6.25V7h12v-.75c0-1.74-1.14-3.18-2.47-4.09zM10 5H9V4h1v1zm5 0h-1V4h1v1z" />
                </svg>
              )}
              {p === "ios" ? "iPhone" : "Android"}
            </button>
          ))}
        </div>
      </div>
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/85 backdrop-blur-sm p-4"
          onClick={() => setLightboxOpen(false)}
        >
          <div
            className="relative flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <Image
              src={BASE + current.img}
              alt={current.label}
              width={420}
              height={910}
              className="global-border-radius max-h-[88vh] w-auto h-auto object-contain shadow-2xl"
            />
            <button
              onClick={() => setLightboxOpen(false)}
              aria-label="Close"
              className="absolute -top-3 -right-3 w-8 h-8 rounded-full
                bg-white/15 backdrop-blur-sm border border-white/20
                flex items-center justify-center cursor-pointer
                hover:bg-white/25 transition-colors duration-150
                focus:outline-none focus:ring-2 focus:ring-white/50"
            >
              <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                <path d="M1 1l9 9M10 1L1 10" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <p className="mt-4 text-white/40 text-xs tracking-wide">
            Press <kbd className="font-mono bg-white/10 px-1.5 py-0.5 rounded text-white/60">Esc</kbd> or click outside to close
          </p>
        </div>
      )}

      <div className="lg:max-w-4xl xl:max-w-6xl 3xl:max-w-[1432px] mx-auto px-4 lg:px-0 pt-8 sm:pt-12 pb-24 flex gap-10 lg:gap-20 items-start">
        <aside className="hidden lg:flex flex-col w-[200px] lg:w-[216px] flex-shrink-0 sticky top-[74px]">
          {!lockedSection && (
            <>
              <p
                className="text-xs 3xl:text-base tracking-[0.18em] text-[#999] uppercase mb-2"
              >
                Section
              </p>
              <div className="flex flex-col gap-[3px] mb-6">
                {(["install", "activate"] as Section[]).map((s) => (
                  <button
                    key={s}
                    onClick={() => switchSection(s)}
                    className={`px-3.5 py-2.5 border rounded-[8px] text-sm 3xl:text-xl font-semibold
                      cursor-pointer text-left tracking-[0.01em] transition-all duration-150
                      ${section === s
                        ? "bg-brand-card border-[#0F0F0F] text-[#F5F3EE]"
                        : "bg-transparent border-[#D8D4C8] text-[#757575] hover:border-[#B8B4A8]"
                      }`}
                  >
                    {s === "install" ? "Installation" : "Activation"}
                  </button>
                ))}
              </div>
            </>
          )}
          <p
            className="text-xs 3xl:text-sm tracking-[0.18em] text-[#999] uppercase mb-2"
          >
            Steps
          </p>
          <nav className="flex flex-col gap-0.5">
            {steps.map((s, i) => {
              const isCur  = i === step;
              const isPast = i < step;
              return (
                <button
                  key={i}
                  onClick={() => jump(i)}
                  className={`flex items-center gap-2.5 px-2.5 py-[7px] rounded-[8px]
                    text-left cursor-pointer transition-colors duration-150 w-full border-none
                    ${isCur ? "bg-brand-grey/60" : "bg-transparent hover:bg-brand-grey/20"}`}
                >
                  <span
                    className={`w-[20px] h-[20px] rounded-full flex items-center justify-center
                      flex-shrink-0 text-xs 3xl:text-sm font-bold border transition-all duration-150
                      ${isCur
                        ? "border-[#0F0F0F] text-[#0F0F0F] bg-transparent"
                        : isPast
                        ? "border-[#757575] text-[#757575] bg-transparent"
                        : "border-[#757575] text-[#757575] bg-transparent"
                      }`}
                  >
                    {i + 1}
                  </span>
                  <span
                    className={`text-sm 3xl:text-xl leading-[1.4] transition-colors duration-150
                      ${isCur ? "font-semibold text-[#0F0F0F]" : isPast ? "font-normal text-[#757575]" : "font-normal text-[#757575]"}`}
                  >
                    {s.title}
                  </span>
                </button>
              );
            })}
          </nav>
          <div className="mt-5 pt-5 border-t border-[#D8D4C8]">
            <div className="flex justify-between mb-2">
              <span
                className="text-xs 3xl:text-sm text-[#5c5c5c]"
              >
                Progress
              </span>
              <span
                className="text-xs 3xl:text-sm font-bold text-brand"
              >
                {step + 1}/{steps.length}
              </span>
            </div>
            <div className="h-[2px] bg-[#D8D4C8] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#0F0F0F] rounded-full transition-all duration-400 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </aside>
        <main className="flex-1 min-w-0">

          {!lockedSection && (
            <div className="lg:hidden mb-5 flex flex-col gap-2 max-w-sm mx-auto">
              <div className="flex global-border-radius border border-[#D8D4C8] overflow-hidden">
                {(["install", "activate"] as Section[]).map((s) => (
                  <button
                    key={s}
                    onClick={() => switchSection(s)}
                    className={`flex-1 py-2.5 text-sm 3xl:text-xl font-semibold cursor-pointer transition-colors
                      ${section === s ? "bg-[#0F0F0F] text-[#F5F3EE]" : "bg-transparent text-[#595959]"}`}
                  >
                    {s === "install" ? "Install" : "Activate"}
                  </button>
                ))}
              </div>
            </div>
          )}
          {lockedSection === "activate" && (
            <p className="mb-5 text-sm 3xl:text-xl text-[#595959] text-center lg:text-left">
              Haven&apos;t installed your eSIM yet?{" "}
              <Link href="/travel-esim/install-esim" className="font-semibold text-[#0F0F0F] underline">
                Start with the installation guide
              </Link>
            </p>
          )}
            <div className="mb-7">
              <VideoSection platform={platform} />
            </div>

          <div className="mb-6">
            <p
              className="text-xs 3xl:text-sm tracking-[0.2em] text-brand-card uppercase mb-2 m-0 text-center lg:text-left"
            >
              Step {step + 1} of {steps.length}&nbsp;&mdash;&nbsp;
              {section === "install" ? "Installation" : "Activation"}
            </p>
            <h2
              className="text-xl sm:text-2xl lg:text-[30px] 3xl:text-4xl font-bold tracking-[-0.02em] text-[#0F0F0F] leading-[1.15] m-0 text-center lg:text-left"
            >
              {current.title}
            </h2>
          </div>
          <div className="flex flex-col lg:flex-row gap-7 lg:gap-10 items-center lg:items-start">

            <div className="relative flex-shrink-0">
              {steps.map((s, i) => (
                <Image
                  key={i}
                  src={BASE + s.img}
                  alt={s.label}
                  width={300}
                  height={650}
                  className={`global-border-radius w-[148px] sm:w-[180px] lg:w-[220px] xl:w-[260px] min-[1400px]:w-[310px] h-auto ${
                    i === step ? "" : "hidden"
                  }`}
                  priority={i === 0}
                />
              ))}
              <button
                onClick={() => setLightboxOpen(true)}
                aria-label="View full size"
                className="absolute bottom-3 right-3
                  flex items-center gap-[5px] px-2.5 py-1.5 rounded-full
                  bg-white shadow-md border border-black/8
                  text-xs 3xl:text-sm font-semibold text-[#222] tracking-[0.01em]
                  hover:bg-[#F7F7F7] hover:shadow-lg
                  transition-all duration-150 cursor-pointer
                  focus:outline-none focus:ring-2 focus:ring-black/20"
              >
                <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                  <path d="M7 1h3v3M4 10H1V7M10 1L6.5 4.5M1 10l3.5-3.5" stroke="#222" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Expand
              </button>
            </div>

            <div className="flex-1 min-w-0 flex flex-col gap-5">
              {steps.map((s, i) => (
                <div key={i} className={i === step ? "flex flex-col gap-5" : "hidden"}>
                  <p className="text-sm 3xl:text-xl text-[#2A2A2A] leading-[1.82] m-0">
                    {s.desc}
                  </p>
                  <div className="border-l-2 border-[#C8C4B8] pl-4">
                    <p
                      className="text-xs 3xl:text-sm tracking-[0.15em] text-[#757575] uppercase mb-1.5 m-0"
                    >
                      Note
                    </p>
                    <p className="text-sm 3xl:text-xl text-[#595959] leading-[1.72] m-0">
                      {s.tip}
                    </p>
                  </div>
                </div>
              ))}
              <div className="flex gap-2.5">
                {!isFirst && (
                  <button
                    onClick={back}
                    className="px-5 py-3 bg-transparent border border-[#C8C4B8] rounded-[8px]
                      text-sm 3xl:text-xl font-semibold text-[#757575] cursor-pointer flex items-center gap-1.5
                      hover:border-[#A8A49C] transition-colors duration-150"
                  >
                    ← Back
                  </button>
                )}

                {!isLast ? (
                  <button
                    onClick={advance}
                    className="flex-1 px-5 py-3 bg-[#0F0F0F] border border-[#0F0F0F] rounded-[8px]
                      text-sm 3xl:text-xl font-bold text-[#F5F3EE] cursor-pointer
                      flex items-center justify-center gap-1.5
                      hover:opacity-90 transition-opacity duration-150"
                  >
                    Continue →
                  </button>
                ) : section === "install" ? (
                  lockedSection === "install" ? (
                    <Link
                      href="/travel-esim/activate-esim"
                      className="flex-1 px-5 py-3 bg-[#0F0F0F] border border-[#0F0F0F] rounded-[8px]
                        text-sm 3xl:text-xl font-bold text-[#F5F3EE] cursor-pointer
                        flex items-center justify-center gap-1.5
                        hover:opacity-90 transition-opacity duration-150"
                    >
                      Continue to Activation →
                    </Link>
                  ) : (
                    <button
                      onClick={() => switchSection("activate")}
                      className="flex-1 px-5 py-3 bg-[#0F0F0F] border border-[#0F0F0F] rounded-[8px]
                        text-sm 3xl:text-xl font-bold text-[#F5F3EE] cursor-pointer
                        flex items-center justify-center gap-1.5
                        hover:opacity-90 transition-opacity duration-150"
                    >
                      Continue to Activation →
                    </button>
                  )
                ) : (
                  <div
                    className="flex-1 px-5 py-3 bg-[#F0F7F0] border border-[#A5D6A7] rounded-[8px]
                      text-sm 3xl:text-xl font-bold text-[#2E7D32] flex items-center justify-center gap-2"
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <circle cx="7" cy="7" r="6" stroke="#2E7D32" strokeWidth="1.5" />
                      <path d="M4 7l2.5 2.5 4-4" stroke="#2E7D32" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                    You&apos;re all set!
                  </div>
                )}
              </div>
              {isLast && (
                <div className="px-5 py-4 bg-[#FAFAF7] border border-[#D8D4C8] rounded-[10px]">
                  {section === "install" ? (
                    <>
                      <p className="text-sm 3xl:text-xl font-bold text-[#0F0F0F] mb-1 m-0">
                        Installation complete.
                      </p>
                      <p className="text-sm 3xl:text-xl text-[#757575] m-0 leading-relaxed">
                        Your eSIM profile is now installed. Continue to the Activation section to turn on the line.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm 3xl:text-xl font-bold text-[#0F0F0F] mb-1 m-0">
                        You&apos;re connected.
                      </p>
                      <p className="text-sm 3xl:text-xl text-[#757575] m-0 leading-relaxed">
                        Open your browser to confirm the connection is live. If it doesn&apos;t connect, toggle Aeroplane mode on then off to refresh.
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="lg:hidden mt-4 h-[2px] bg-[#D8D4C8] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#0F0F0F] rounded-full transition-all duration-400 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>

        </main>
      </div>
    </div>
  );
}
